"use client";

import { authorizedFetch } from "@/lib/auth/authorized-fetch";

import Link from "next/link";
import { Copy, Loader2, RotateCcw, Square } from "lucide-react";
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { useAuth } from "@/context/AuthContext";
import type { PrismCatalog } from "@/lib/prism/catalog";
import type { UsePrismStateReturn } from "@/modules/prism/hooks/usePrismState";
import { sessionTitleFromMessage } from "@/lib/prism/session-types";
import { emitPrismWorkspaceUpdate } from "@/lib/prism/dock/workspace-sync";
import type {
    PrismCitation,
    PrismChatMeta,
    PrismPlan,
    PrismWorkflow,
    PrismWorkflowCard,
} from "@/lib/prism/types";
import { PrismComposer, type ComposerAttachment } from "./PrismComposer";
import { PrismMarkdown } from "./PrismMarkdown";
import { SaviraMark } from "./SaviraMark";
import type { PageContextDescriptor } from "@/lib/prism/page-context/types";

export type PlanStepEvent = {
    index: number;
    projectStepId?: string;
    status: "running" | "done";
    workflow: PrismWorkflow;
    label: string;
};

export interface PrismChatPanelHandle {
    runPlan: (params: { plan: PrismPlan; text: string; stream?: boolean }) => Promise<void>;
}

function greeting(name?: string): string {
    const h = new Date().getHours();
    const part = h < 12 ? "Morning" : h < 18 ? "Afternoon" : "Evening";
    const first = name?.trim().split(/\s+/)[0];
    return first ? `${part}, ${first}` : part;
}

function SourcesDisclosure({ citations }: { citations: PrismCitation[] }) {
    const [open, setOpen] = useState(false);
    if (!citations.length) return null;
    return (
        <div className="mt-2">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="text-[11px] text-ink-3 hover:text-ink-1 transition-colors"
            >
                {open ? "Hide" : "Show"} sources ({citations.length})
            </button>
            {open ? (
                <ul className="mt-1.5 space-y-1.5 border-l-2 border-surface-1 pl-3">
                    {citations.map((c) => (
                        <li key={c.id} className="text-[11px] text-ink-2 leading-snug">
                            {c.url ? (
                                <Link href={c.url} className="font-medium text-accent hover:underline">
                                    {c.title}
                                </Link>
                            ) : (
                                <span className="font-medium text-ink-1">{c.title}</span>
                            )}
                            {c.company ? (
                                <span className="text-ink-3"> · {c.company}</span>
                            ) : null}
                            {c.snippet ? (
                                <p className="text-ink-3 mt-0.5 line-clamp-2">{c.snippet}</p>
                            ) : null}
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}

function WorkflowCards({
    cards,
    onConfirmProposal,
    onConfirmPlan,
    confirming,
}: {
    cards: PrismWorkflowCard[];
    onConfirmProposal?: (workflow: PrismWorkflow, meta?: Record<string, unknown>) => void;
    onConfirmPlan?: (plan: PrismPlan) => void;
    confirming?: boolean;
}) {
    if (!cards.length) return null;
    return (
        <div className="mt-3 space-y-2">
            {cards.map((card, i) => {
                if (card.type === "plan") {
                    const plan = card.meta?.plan as PrismPlan | undefined;
                    const stepLabels = (card.meta?.steps as string[] | undefined) ?? [];
                    const confirmLabel =
                        (card.meta?.confirmLabel as string | undefined) ?? "Run plan";
                    return (
                        <div
                            key={i}
                            className="rounded-xl border border-accent-line bg-accent-bg/40 px-3 py-2.5"
                        >
                            <p className="text-sm font-semibold text-ink-0">{card.title}</p>
                            {card.subtitle ? (
                                <p className="text-xs text-ink-2 mt-0.5">{card.subtitle}</p>
                            ) : null}
                            {stepLabels.length ? (
                                <ol className="mt-2 space-y-1 list-decimal list-inside text-xs text-ink-2">
                                    {stepLabels.map((label, idx) => (
                                        <li key={idx}>{label}</li>
                                    ))}
                                </ol>
                            ) : null}
                            {plan && onConfirmPlan ? (
                                <button
                                    type="button"
                                    disabled={confirming}
                                    onClick={() => onConfirmPlan(plan)}
                                    className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-1.5 text-xs font-medium text-accent-ink hover:bg-accent-hover disabled:opacity-50"
                                >
                                    {confirming ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : null}
                                    {confirmLabel}
                                </button>
                            ) : null}
                        </div>
                    );
                }

                if (card.type === "proposal") {
                    const workflow = card.meta?.workflow as PrismWorkflow | undefined;
                    const confirmLabel =
                        (card.meta?.confirmLabel as string | undefined) ?? "Confirm";
                    return (
                        <div
                            key={i}
                            className="rounded-xl border border-accent-line bg-accent-bg/40 px-3 py-2.5"
                        >
                            <p className="text-sm font-semibold text-ink-0">{card.title}</p>
                            {card.subtitle ? (
                                <p className="text-xs text-ink-2 mt-0.5">{card.subtitle}</p>
                            ) : null}
                            {workflow && onConfirmProposal ? (
                                <button
                                    type="button"
                                    disabled={confirming}
                                    onClick={() => onConfirmProposal(workflow, card.meta)}
                                    className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-1.5 text-xs font-medium text-accent-ink hover:bg-accent-hover disabled:opacity-50"
                                >
                                    {confirming ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : null}
                                    {confirmLabel}
                                </button>
                            ) : null}
                        </div>
                    );
                }

                const body = (
                    <div className="rounded-xl border border-[var(--border-hairline)] bg-surface-0 px-3 py-2 text-left hover:border-accent-line transition-colors">
                        <p className="text-sm font-semibold text-ink-0">{card.title}</p>
                        {card.subtitle ? (
                            <p className="text-xs text-ink-2 mt-0.5">{card.subtitle}</p>
                        ) : null}
                    </div>
                );
                return card.href ? (
                    <Link key={i} href={card.href}>
                        {body}
                    </Link>
                ) : (
                    <div key={i}>{body}</div>
                );
            })}
        </div>
    );
}

function MessageActions({
    onCopy,
    onRegenerate,
}: {
    onCopy: () => void;
    onRegenerate?: () => void;
}) {
    return (
        <div className="mt-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
                type="button"
                onClick={onCopy}
                className="p-1 rounded-md text-ink-3 hover:bg-surface-1 hover:text-ink-1"
                aria-label="Copy"
            >
                <Copy className="w-3.5 h-3.5" />
            </button>
            {onRegenerate ? (
                <button
                    type="button"
                    onClick={onRegenerate}
                    className="p-1 rounded-md text-ink-3 hover:bg-surface-1 hover:text-ink-1"
                    aria-label="Regenerate"
                >
                    <RotateCcw className="w-3.5 h-3.5" />
                </button>
            ) : null}
        </div>
    );
}

interface PrismChatPanelProps {
    prism: UsePrismStateReturn;
    catalog: PrismCatalog;
    companyHint?: string;
    input: string;
    onInputChange: (v: string) => void;
    attachments: ComposerAttachment[];
    onAttachmentsChange: (a: ComposerAttachment[]) => void;
    activeSkillId: string | null;
    onActiveSkillChange: (id: string | null) => void;
    pageContext?: PageContextDescriptor | null;
    onPlanStep?: (event: PlanStepEvent) => void;
}

type ChatReplyPayload = {
    markdown?: string;
    cards?: PrismWorkflowCard[];
    citations?: PrismCitation[];
    meta?: PrismChatMeta | null;
};

type DispatchedInfo = { module?: string | null; workflow?: string | null };

type StreamDonePayload = {
    reply?: ChatReplyPayload;
    dispatched?: DispatchedInfo;
    plan?: PrismPlan | null;
    pausedAt?: number | null;
};

export const PrismChatPanel = forwardRef<PrismChatPanelHandle, PrismChatPanelProps>(
    function PrismChatPanel(
        {
            prism,
            catalog,
            companyHint,
            input,
            onInputChange,
            attachments,
            onAttachmentsChange,
            activeSkillId,
            onActiveSkillChange,
            pageContext,
            onPlanStep,
        },
        ref,
    ) {
        const { hydrated, state, activeSession, ensureActiveSession, appendMessages } = prism;
        const { user } = useAuth();

        const [loading, setLoading] = useState(false);
        const [confirming, setConfirming] = useState(false);
        const [error, setError] = useState<string | null>(null);
        const [streamingText, setStreamingText] = useState("");
        const [planProgress, setPlanProgress] = useState<{
            index: number;
            total: number;
            label: string;
        } | null>(null);
        const [enabledConnectorIds, setEnabledConnectorIds] = useState<string[]>([]);
        const abortRef = useRef<AbortController | null>(null);
        const bottomRef = useRef<HTMLDivElement>(null);

        const hasMessages = (activeSession?.messages.length ?? 0) > 0;

        useEffect(() => {
            if (hasMessages || streamingText) {
                bottomRef.current?.scrollIntoView({ behavior: "smooth" });
            }
        }, [activeSession?.messages.length, loading, streamingText, hasMessages]);

        const builtinSkillIds = useMemo(
            () => new Set(catalog.skills.map((s) => s.id)),
            [catalog.skills],
        );

        const buildHistory = useCallback(
            (excludeLastAssistant?: boolean) => {
                let msgs = activeSession?.messages ?? [];
                if (excludeLastAssistant && msgs.length && msgs[msgs.length - 1]?.role === "assistant") {
                    msgs = msgs.slice(0, -1);
                }
                return msgs.slice(-20).map((m) => ({
                    role: m.role,
                    content: m.content,
                }));
            },
            [activeSession?.messages],
        );

        const stopGeneration = useCallback(() => {
            abortRef.current?.abort();
            abortRef.current = null;
            setLoading(false);
            setStreamingText("");
            setPlanProgress(null);
        }, []);

        const finalizeAssistant = useCallback(
            (
                sessionId: string,
                reply: ChatReplyPayload,
                dispatched?: DispatchedInfo,
            ) => {
                const assistantTime = new Date().toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                });
                appendMessages(sessionId, [
                    {
                        role: "assistant",
                        content: reply.markdown ?? "",
                        time: assistantTime,
                        cards: reply.cards,
                        citations: reply.citations,
                        meta: reply.meta,
                        dispatchedModule: dispatched?.module,
                    },
                ]);

                const workflow = dispatched?.workflow ?? undefined;
                if (workflow && workflow.includes(".edit_")) {
                    emitPrismWorkspaceUpdate({
                        module: dispatched?.module ?? undefined,
                        workflow,
                        companyClientId: pageContext?.companyClientId ?? pageContext?.entityId,
                    });
                }
            },
            [appendMessages, pageContext?.companyClientId, pageContext?.entityId],
        );

        const runChat = useCallback(
            async (params: {
                text: string;
                skillId?: string | null;
                confirmWorkflow?: PrismWorkflow;
                plan?: PrismPlan;
                planCursor?: number;
                confirmPlan?: boolean;
                outgoingAttachments?: ComposerAttachment[];
                stream?: boolean;
                skipUserAppend?: boolean;
                excludeLastAssistant?: boolean;
            }) => {
                const {
                    text,
                    skillId = null,
                    confirmWorkflow,
                    plan,
                    planCursor = 0,
                    confirmPlan,
                    outgoingAttachments = [],
                    stream = true,
                    skipUserAppend = false,
                    excludeLastAssistant = false,
                } = params;

                if (!text || loading) return;

                setError(null);
                setLoading(true);
                setStreamingText("");
                if (confirmPlan) {
                    setPlanProgress({
                        index: planCursor + 1,
                        total: plan?.steps.length ?? 1,
                        label: plan?.steps[planCursor]?.label ?? "Running plan",
                    });
                } else {
                    setPlanProgress(null);
                }

                const controller = new AbortController();
                abortRef.current = controller;

                const sessionId = ensureActiveSession();
                const now = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

                if (!confirmWorkflow && !confirmPlan && !skipUserAppend) {
                    appendMessages(
                        sessionId,
                        [{ role: "user", content: text, time: now }],
                        sessionTitleFromMessage(text),
                    );
                }

                const useStream = stream && (!confirmWorkflow || Boolean(confirmPlan));

                const body = {
                    message: text,
                    scope: state.scope,
                    companyHint: state.scope === "company" ? companyHint : undefined,
                    modelTier: state.selectedModelId,
                    skillId: skillId && builtinSkillIds.has(skillId) ? skillId : undefined,
                    confirmWorkflow,
                    plan,
                    planCursor,
                    confirmPlan,
                    attachments: outgoingAttachments.map((a) => ({
                        kind: a.kind,
                        mime: a.mime,
                        data: a.data,
                    })),
                    history: buildHistory(excludeLastAssistant),
                    stream: useStream,
                    pageContext: pageContext ?? undefined,
                    connectors: enabledConnectorIds.length ? enabledConnectorIds : undefined,
                };

                try {
                    const res = await authorizedFetch("/api/prism/chat", {
                        method: "POST",
                        credentials: "include",
                        headers: { "content-type": "application/json" },
                        body: JSON.stringify(body),
                        signal: controller.signal,
                    });

                    const contentType = res.headers.get("content-type") ?? "";

                    if (contentType.includes("text/event-stream") && res.body) {
                        const reader = res.body.getReader();
                        const decoder = new TextDecoder();
                        let buffer = "";
                        let accumulated = "";
                        let finalPayload: StreamDonePayload | null = null;

                        while (true) {
                            const { done, value } = await reader.read();
                            if (done) break;
                            buffer += decoder.decode(value, { stream: true });
                            const parts = buffer.split("\n\n");
                            buffer = parts.pop() ?? "";

                            for (const part of parts) {
                                const lines = part.split("\n");
                                let event = "message";
                                let dataLine = "";
                                for (const line of lines) {
                                    if (line.startsWith("event:")) event = line.slice(6).trim();
                                    if (line.startsWith("data:")) dataLine = line.slice(5).trim();
                                }
                                if (!dataLine) continue;
                                try {
                                    const parsed = JSON.parse(dataLine) as Record<string, unknown>;
                                    if (event === "token" && typeof parsed.text === "string") {
                                        accumulated += parsed.text;
                                        setStreamingText(accumulated);
                                    } else if (event === "step") {
                                        const stepEvent = parsed as unknown as PlanStepEvent;
                                        onPlanStep?.(stepEvent);
                                        if (plan) {
                                            setPlanProgress({
                                                index: stepEvent.index + 1,
                                                total: plan.steps.length,
                                                label: stepEvent.label,
                                            });
                                        }
                                    } else if (event === "done") {
                                        finalPayload = parsed as StreamDonePayload;
                                    } else if (event === "error") {
                                        throw new Error(String(parsed.error ?? "Stream failed"));
                                    }
                                } catch (e) {
                                    if (event === "error") throw e;
                                }
                            }
                        }

                        const reply = finalPayload?.reply ?? { markdown: accumulated };
                        if (!reply.markdown && accumulated) reply.markdown = accumulated;
                        finalizeAssistant(sessionId, reply, finalPayload?.dispatched ?? undefined);
                    } else {
                        const data = (await res.json()) as {
                            success?: boolean;
                            reply?: ChatReplyPayload;
                            dispatched?: DispatchedInfo;
                            error?: string;
                            proposed?: boolean;
                        };
                        if (!res.ok || !data.success) {
                            throw new Error(data.error ?? "Chat failed");
                        }
                        finalizeAssistant(sessionId, data.reply ?? {}, data.dispatched);
                    }
                } catch (e) {
                    if ((e as Error).name !== "AbortError") {
                        setError(e instanceof Error ? e.message : "Something went wrong");
                    }
                } finally {
                    setLoading(false);
                    setStreamingText("");
                    setPlanProgress(null);
                    abortRef.current = null;
                }
            },
            [
                loading,
                ensureActiveSession,
                appendMessages,
                state.scope,
                state.selectedModelId,
                companyHint,
                builtinSkillIds,
                enabledConnectorIds,
                buildHistory,
                finalizeAssistant,
                pageContext,
                onPlanStep,
            ],
        );

        useImperativeHandle(
            ref,
            () => ({
                runPlan: async ({ plan, text, stream = true }) => {
                    await runChat({
                        text,
                        plan,
                        planCursor: 0,
                        confirmPlan: true,
                        stream,
                        skipUserAppend: false,
                    });
                },
            }),
            [runChat],
        );

        const send = useCallback(
            async (text: string) => {
                const outgoingAttachments = attachments;
                const outgoingSkill = activeSkillId;
                onInputChange("");
                onAttachmentsChange([]);
                onActiveSkillChange(null);
                await runChat({
                    text,
                    skillId: outgoingSkill,
                    outgoingAttachments,
                    stream: true,
                });
            },
            [
                attachments,
                activeSkillId,
                onInputChange,
                onAttachmentsChange,
                onActiveSkillChange,
                runChat,
            ],
        );

        const confirmPlan = useCallback(
            async (plan: PrismPlan) => {
                const lastUser = [...(activeSession?.messages ?? [])]
                    .reverse()
                    .find((m) => m.role === "user");
                const text = lastUser?.content ?? input;
                if (!text) return;
                setConfirming(true);
                try {
                    await runChat({
                        text,
                        plan,
                        planCursor: 0,
                        confirmPlan: true,
                        stream: true,
                    });
                } finally {
                    setConfirming(false);
                }
            },
            [activeSession?.messages, input, runChat],
        );

        const confirmProposal = useCallback(
            async (workflow: PrismWorkflow, meta?: Record<string, unknown>) => {
                const lastUser = [...(activeSession?.messages ?? [])]
                    .reverse()
                    .find((m) => m.role === "user");
                const text = lastUser?.content ?? input;
                if (!text) return;

                const plan = meta?.plan as PrismPlan | undefined;
                const planCursor =
                    typeof meta?.planCursor === "number" ? meta.planCursor : undefined;

                setConfirming(true);
                try {
                    if (plan != null && planCursor != null) {
                        await runChat({
                            text,
                            confirmWorkflow: workflow,
                            plan,
                            planCursor,
                            confirmPlan: true,
                            stream: true,
                        });
                    } else {
                        await runChat({ text, confirmWorkflow: workflow, stream: false });
                    }
                } finally {
                    setConfirming(false);
                }
            },
            [activeSession?.messages, input, runChat],
        );

        const regenerateLast = useCallback(async () => {
            const msgs = activeSession?.messages ?? [];
            const lastUserIdx = [...msgs]
                .map((m, i) => (m.role === "user" ? i : -1))
                .filter((i) => i >= 0)
                .pop();
            if (lastUserIdx == null) return;
            const lastUser = msgs[lastUserIdx];
            await runChat({
                text: lastUser.content,
                stream: true,
                skipUserAppend: true,
                excludeLastAssistant: true,
            });
        }, [activeSession?.messages, runChat]);

        if (!hydrated) {
            return (
                <div className="flex-1 flex items-center justify-center text-sm text-ink-3">
                    Loading Prism…
                </div>
            );
        }

        const composer = (variant: "hero" | "docked") => (
            <PrismComposer
                prism={prism}
                catalog={catalog}
                loading={loading}
                value={input}
                onValueChange={onInputChange}
                onSend={send}
                attachments={attachments}
                onAttachmentsChange={onAttachmentsChange}
                activeSkillId={activeSkillId}
                onActiveSkillChange={onActiveSkillChange}
                companyName={companyHint}
                enabledConnectorIds={enabledConnectorIds}
                onEnabledConnectorIdsChange={setEnabledConnectorIds}
                variant={variant}
            />
        );

        if (!hasMessages && !streamingText) {
            return (
                <div className="flex flex-col flex-1 min-w-0 min-h-0 bg-surface-0">
                    <div className="flex-1 flex flex-col items-center justify-center px-4">
                        <div className="w-full max-w-2xl">
                            <div className="mb-7 flex items-center justify-center gap-3">
                                <SaviraMark className="h-9 w-auto" />
                                <h1 className="text-3xl md:text-4xl font-serif text-ink-0">
                                    {greeting(user?.name)}
                                </h1>
                            </div>
                            {composer("hero")}
                            <div className="mt-5 flex flex-wrap justify-center gap-2">
                                {catalog.skills.slice(0, 4).map((s) => (
                                    <button
                                        key={s.id}
                                        type="button"
                                        onClick={() => {
                                            onActiveSkillChange(s.id);
                                            onInputChange(
                                                s.promptTemplate
                                                    .replace("{company}", companyHint ?? "the company")
                                                    .replace("{theme}", ""),
                                            );
                                        }}
                                        className="rounded-full border border-[var(--border-hairline)] bg-surface-0 px-3 py-1.5 text-xs text-ink-2 hover:border-accent-line hover:text-ink-0 transition-colors"
                                    >
                                        {s.name}
                                    </button>
                                ))}
                            </div>
                            {error ? (
                                <p className="mt-3 text-center text-sm text-bad">{error}</p>
                            ) : null}
                        </div>
                    </div>
                </div>
            );
        }

        const lastAssistantIdx = (activeSession?.messages ?? [])
            .map((m, i) => (m.role === "assistant" ? i : -1))
            .filter((i) => i >= 0)
            .pop();

        return (
            <div className="flex flex-col flex-1 min-w-0 min-h-0 bg-surface-0">
                <div className="flex-1 overflow-y-auto px-4 md:px-6 py-6 min-h-0">
                    <div className="max-w-3xl mx-auto space-y-6">
                        {activeSession?.messages.map((msg, i) =>
                            msg.role === "user" ? (
                                <div key={i} className="flex justify-end">
                                    <div className="max-w-[85%] rounded-2xl bg-surface-1 border border-[var(--border-hairline)] px-4 py-2.5 text-[15px] text-ink-0 whitespace-pre-wrap leading-relaxed">
                                        {msg.content}
                                    </div>
                                </div>
                            ) : (
                                <div key={i} className="flex gap-3 group">
                                    <div className="mt-0.5 shrink-0">
                                        <SaviraMark className="h-6 w-auto" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        {msg.dispatchedModule && msg.dispatchedModule !== "prism" ? (
                                            <p className="text-[10px] uppercase tracking-wide text-ink-3 mb-1">
                                                via {msg.dispatchedModule}
                                            </p>
                                        ) : null}
                                        <PrismMarkdown content={msg.content} />
                                        {msg.cards?.length ? (
                                            <WorkflowCards
                                                cards={msg.cards}
                                                onConfirmProposal={confirmProposal}
                                                onConfirmPlan={confirmPlan}
                                                confirming={confirming}
                                            />
                                        ) : null}
                                        {msg.citations?.length ? (
                                            <SourcesDisclosure citations={msg.citations} />
                                        ) : null}
                                        <MessageActions
                                            onCopy={() => void navigator.clipboard.writeText(msg.content)}
                                            onRegenerate={
                                                i === lastAssistantIdx
                                                    ? () => void regenerateLast()
                                                    : undefined
                                            }
                                        />
                                    </div>
                                </div>
                            ),
                        )}
                        {loading && streamingText ? (
                            <div className="flex gap-3">
                                <div className="mt-0.5 shrink-0">
                                    <SaviraMark className="h-6 w-auto" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <PrismMarkdown content={streamingText} />
                                </div>
                            </div>
                        ) : loading ? (
                            <div className="flex items-center gap-2 text-sm text-ink-3">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                {planProgress ? (
                                    <span>
                                        Step {planProgress.index}/{planProgress.total}:{" "}
                                        {planProgress.label}
                                    </span>
                                ) : (
                                    "Thinking…"
                                )}
                                <button
                                    type="button"
                                    onClick={stopGeneration}
                                    className="ml-2 inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs text-ink-2 hover:bg-surface-1"
                                >
                                    <Square className="w-3 h-3" />
                                    Stop
                                </button>
                            </div>
                        ) : null}
                        <div ref={bottomRef} />
                    </div>
                </div>

                {error ? (
                    <p className="px-6 text-sm text-bad shrink-0 max-w-3xl mx-auto w-full">
                        {error}
                    </p>
                ) : null}

                <div className="shrink-0 px-4 pb-4 pt-1">
                    <div className="max-w-3xl mx-auto">{composer("docked")}</div>
                </div>
            </div>
        );
    },
);
